import { isNativeApp, isBundledNativeOrigin } from './appOrigin';

/**
 * Whether the user is away on a Stripe page and owes us a refresh when they come back.
 *
 * Module state rather than a store field so that it cannot be forgotten: every route to a
 * Stripe page already goes through `openCheckoutUrl`, so a fourth call site added later
 * gets the return handling for free. It is deliberately *not* the same thing as
 * `isCreatingCheckoutSessionForPlanId`, which is only the spinner on the button while the
 * session is being created and is over by the time the browser opens.
 *
 * Never set on the web, where the page unloads and returns through a fresh page load
 * instead.
 */
let pendingStripeReturn = false;

/**
 * Sends the user to a Stripe-hosted page — Checkout or the billing portal.
 *
 * On the web this is `window.location.href`, exactly as before.
 *
 * ## Why the packaged app needs its own branch
 *
 * `window.location.href = checkoutUrl` does not navigate the WebView in the packaged app;
 * Capacitor intercepts it first. iOS cancels the navigation in
 * `WebViewDelegationHandler.decidePolicyFor` and calls `UIApplication.shared.open`, and
 * Android routes it through `BridgeWebViewClient.shouldOverrideUrlLoading` to
 * `Bridge.launchIntent`. So Stripe already opens, in Safari or Chrome, as a **separate app**.
 *
 * That works, but it is the OS deciding rather than us, and it costs three things:
 *
 *   - The user leaves the app entirely and has to find their own way back.
 *   - We hold no handle on the browser, so we cannot dismiss it when the purchase completes
 *     — which is what the deep-link return trip will need (see
 *     docs/capacitor-mobile-app-feasibility.md §5 item 6).
 *   - The caller cannot tell that a hand-off happened, so it cannot clear its own loading
 *     state; on the web that state disappears with the page load that never comes here.
 *
 * Opening deliberately through `@capacitor/browser` gives an in-app SFSafariViewController /
 * Custom Tab instead: the app stays alive behind it, the user gets a Done button, and
 * `Browser.close()` becomes available later. The same reasoning, and the same plugin, as
 * `nativeAuthBrowser.ts` — this is the payments half of the pattern login already uses.
 *
 * Gated on the *bundled* origin, not merely on Capacitor being present, for the reason
 * spelled out in `appOrigin.ts`: the Phase 0 diagnostic runs the bridge at a real https
 * origin where the ordinary navigation is correct.
 *
 * `@capacitor/browser` is imported dynamically so web builds never pull the native plugin
 * into their bundle.
 *
 * @returns `true` when the URL was handed to the system browser and the WebView stayed put,
 *          so the caller still owns its loading state. `false` when the page is navigating
 *          away and the caller's state is about to be discarded with it.
 */
export async function openCheckoutUrl(url: string): Promise<boolean> {
   if (!isNativeApp() || !isBundledNativeOrigin()) {
      window.location.href = url;

      return false;
   }

   const { Browser } = await import('@capacitor/browser');

   pendingStripeReturn = true;

   await Browser.open({ url });

   return true;
}

/**
 * Reads the flag and clears it, so the first responder wins.
 *
 * `plugins/nativeCheckoutReturn.client.ts` listens on two events that can both fire for one
 * purchase — dismissing the in-app browser, and the app returning to the foreground. The
 * refresh should happen once, and which event arrives first depends on how the OS chose to
 * show the Stripe page.
 */
export function consumePendingStripeReturn(): boolean {
   const wasPending = pendingStripeReturn;

   pendingStripeReturn = false;

   return wasPending;
}
